/**
 * SSO Configuration Dialog — S7 of FEATURE_ROADMAP §19 Phase 1.
 *
 * Tenant admins configure OIDC SSO here. Two paths:
 *   1. Preferred: paste a .well-known/openid-configuration discovery URL +
 *      client_id + client_secret. The server fetches the discovery doc and
 *      auto-populates issuer / authorization / token / jwks endpoints.
 *   2. Manual: enter issuer_url + other fields when the IdP doesn't publish
 *      a discovery URL (rare; most OIDC providers do).
 *
 * "Test Connection" is the single most important UX element — it runs a
 * full no-side-effect OIDC flow against the saved/draft config and surfaces
 * what ARIAS extracted (email, name, groups, resolved role). Tenant admins
 * MUST see a green test before "Enable for users" is allowed to flip on.
 * Backend enforces this gate too.
 *
 * Client secret semantics:
 *   - GET returns '***' (server never sends plaintext)
 *   - PUT with '***' = leave existing secret unchanged
 *   - PUT with new value = rotate
 *   - PUT with empty = clear
 */
import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem,
  Select, Switch, TextField, Tooltip, Typography, Alert, Link as MuiLink,
} from '@mui/material'
import { Lock, OpenInNew, CheckCircle, Cancel, Settings as SettingsIcon } from '@mui/icons-material'
import { tenantApi } from '../services/api'

interface Props {
  open: boolean
  onClose: () => void
  tenantId: string
  onSaved?: () => void
}

interface SsoConfig {
  tenant_id?: string
  idp_type: 'oidc' | 'saml'
  enabled: boolean
  sso_slug: string
  oidc_discovery_url: string
  oidc_issuer_url: string
  oidc_client_id: string
  oidc_client_secret: string  // '***' from GET, or new value, or empty
  attr_email: string
  attr_name: string
  attr_groups: string
  default_role: 'viewer' | 'analyst' | 'manager' | 'admin'
  group_role_mapping: Record<string, string>
  jit_provisioning_enabled: boolean
  last_test_connection_at?: string | null
  last_test_connection_ok?: boolean | null
}

const ROLES = ['viewer', 'analyst', 'manager', 'admin'] as const

const emptyConfig: SsoConfig = {
  idp_type: 'oidc',
  enabled: false,
  sso_slug: '',
  oidc_discovery_url: '',
  oidc_issuer_url: '',
  oidc_client_id: '',
  oidc_client_secret: '',
  attr_email: 'email',
  attr_name: 'name',
  attr_groups: 'groups',
  default_role: 'viewer',
  group_role_mapping: {},
  jit_provisioning_enabled: true,
}

const SsoConfigDialog = ({ open, onClose, tenantId, onSaved }: Props) => {
  const [config, setConfig] = useState<SsoConfig>(emptyConfig)
  const [hasSavedConfig, setHasSavedConfig] = useState(false)
  const [groupMapText, setGroupMapText] = useState('{}')
  const [groupMapError, setGroupMapError] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [error, setError] = useState<string>('')
  const popupRef = useRef<Window | null>(null)

  // Load existing config when opening
  useEffect(() => {
    if (!open || !tenantId) return
    setError(''); setTestResult(null)
    setLoading(true)
    tenantApi.getSsoConfig(tenantId)
      .then(res => {
        const d = res.data
        setConfig({
          ...emptyConfig,
          ...d,
          // Drop server-only timestamps so they don't end up in the PUT body
          oidc_client_secret: d.oidc_client_secret || '',
        })
        setGroupMapText(JSON.stringify(d.group_role_mapping || {}, null, 2))
        setHasSavedConfig(true)
      })
      .catch(e => {
        if (e.response?.status === 404) {
          // No config yet — start from empty
          setConfig(emptyConfig)
          setGroupMapText('{}')
          setHasSavedConfig(false)
        } else {
          setError(e.response?.data?.detail || 'Failed to load SSO config')
        }
      })
      .finally(() => setLoading(false))
  }, [open, tenantId])

  // Listen for the Test Connection popup's postMessage
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      if (!ev.data || ev.data.type !== 'arias_sso_test_result') return
      setTestResult(ev.data.payload || null)
      setTesting(false)
      // Refresh saved config so last_test_connection_at/ok reflects the new result
      if (hasSavedConfig) {
        tenantApi.getSsoConfig(tenantId).then(res => {
          const d = res.data
          setConfig(prev => ({
            ...prev,
            last_test_connection_at: d.last_test_connection_at,
            last_test_connection_ok: d.last_test_connection_ok,
          }))
        }).catch(() => {})
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [hasSavedConfig, tenantId])

  const validateGroupMap = (text: string): boolean => {
    try {
      const parsed = JSON.parse(text || '{}')
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setGroupMapError('Must be a JSON object {"group":"role",...}')
        return false
      }
      const badRoles = Object.values(parsed).filter(v => !ROLES.includes(v as any))
      if (badRoles.length > 0) {
        setGroupMapError(`Invalid role(s): ${badRoles.join(', ')}. Allowed: ${ROLES.join(', ')}`)
        return false
      }
      setGroupMapError('')
      return true
    } catch {
      setGroupMapError('Invalid JSON')
      return false
    }
  }

  const buildPutBody = (): any => {
    if (!validateGroupMap(groupMapText)) {
      throw new Error('Fix group→role mapping JSON first')
    }
    return {
      idp_type: config.idp_type,
      enabled: config.enabled,
      sso_slug: config.sso_slug.trim() || null,
      oidc_discovery_url: config.oidc_discovery_url.trim() || null,
      oidc_issuer_url: config.oidc_issuer_url.trim() || null,
      oidc_client_id: config.oidc_client_id.trim() || null,
      oidc_client_secret: config.oidc_client_secret,  // '***' = unchanged
      attr_email: config.attr_email || 'email',
      attr_name: config.attr_name || 'name',
      attr_groups: config.attr_groups || 'groups',
      default_role: config.default_role,
      group_role_mapping: JSON.parse(groupMapText || '{}'),
      jit_provisioning_enabled: config.jit_provisioning_enabled,
    }
  }

  const handleSave = async () => {
    setError(''); setSaving(true)
    try {
      const body = buildPutBody()
      await tenantApi.upsertSsoConfig(tenantId, body)
      setHasSavedConfig(true)
      onSaved?.()
      // Refresh state from server (gets new last_test_connection reset etc.)
      const res = await tenantApi.getSsoConfig(tenantId)
      setConfig({ ...emptyConfig, ...res.data, oidc_client_secret: res.data.oidc_client_secret || '' })
    } catch (e: any) {
      const detail = e.response?.data?.detail
      setError(typeof detail === 'string' ? detail : (e.message || 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    setError(''); setTestResult(null); setTesting(true)
    try {
      // If user has unsaved changes, we test the DRAFT — otherwise use saved.
      // The simplest heuristic: if hasSavedConfig is true, prefer use_saved=true.
      // Users iterating on a draft can save (which resets last_test) then test.
      const body: any = { tenant_id: tenantId, use_saved: hasSavedConfig }
      if (!hasSavedConfig) {
        body.draft = buildPutBody()
      }
      const res = await tenantApi.startSsoTestConnection(body)
      const authUrl = res.data?.authorization_url
      if (!authUrl) throw new Error('No authorization URL returned')
      // Open the IdP in a popup. After auth, the IdP redirects to our
      // /sso/test-callback which postMessages the diagnostic back here.
      popupRef.current = window.open(
        authUrl, 'arias_sso_test',
        'width=520,height=720,scrollbars=yes,resizable=yes,toolbar=no,location=no',
      )
      if (!popupRef.current) {
        setError('Popup blocked. Allow popups for this site and try again.')
        setTesting(false)
      }
      // Else: wait for postMessage from popup (handled in useEffect)
    } catch (e: any) {
      const detail = e.response?.data?.detail
      setError(typeof detail === 'string' ? detail : (e.message || 'Test Connection failed to start'))
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Remove SSO configuration? Users who only login via SSO will lose access until SSO is reconfigured or their passwords are reset.')) return
    try {
      await tenantApi.deleteSsoConfig(tenantId)
      setConfig(emptyConfig); setGroupMapText('{}'); setHasSavedConfig(false); setTestResult(null)
      onSaved?.()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed')
    }
  }

  const canEnable = !!(config.last_test_connection_ok)
  const enableBlocked = config.enabled && !canEnable

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        Single Sign-On (OIDC)
        <Typography variant="body2" color="text.secondary">
          Configure your tenant's identity provider. Users in your IdP's groups sign in to ARIAS without a separate password.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
              </Grid>
            )}

            {/* Connection */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Connection</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="OIDC discovery URL (recommended)"
                placeholder="https://your-idp.example.com/.well-known/openid-configuration"
                value={config.oidc_discovery_url}
                onChange={e => setConfig({ ...config, oidc_discovery_url: e.target.value })}
                helperText="Server fetches this to discover issuer, JWKS, and endpoints. Leave blank to enter issuer manually below."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Issuer URL"
                placeholder="(auto-populated from discovery)"
                value={config.oidc_issuer_url}
                onChange={e => setConfig({ ...config, oidc_issuer_url: e.target.value })}
                disabled={!!config.oidc_discovery_url}
                helperText={config.oidc_discovery_url ? 'Auto-populated' : 'Only needed if no discovery URL'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="URL slug (optional)"
                placeholder="acme"
                value={config.sso_slug}
                onChange={e => setConfig({ ...config, sso_slug: e.target.value })}
                helperText="2-64 lowercase letters/digits/hyphens. Powers /sso/{slug}/login URLs."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Client ID" required
                value={config.oidc_client_id}
                onChange={e => setConfig({ ...config, oidc_client_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Client secret" type="password"
                value={config.oidc_client_secret}
                onChange={e => setConfig({ ...config, oidc_client_secret: e.target.value })}
                helperText={hasSavedConfig
                  ? "Leave '***' to keep existing. Enter new value to rotate."
                  : "Stored encrypted with the per-environment SSO_ENCRYPTION_KEY."}
              />
            </Grid>

            {/* Attribute mapping */}
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Claim mapping</Typography>
              <Typography variant="caption" color="text.secondary">
                The names of the claims your IdP emits. Defaults match Keycloak / Okta / Azure / Auth0.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Email claim"
                value={config.attr_email}
                onChange={e => setConfig({ ...config, attr_email: e.target.value })}/>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Name claim"
                value={config.attr_name}
                onChange={e => setConfig({ ...config, attr_name: e.target.value })}/>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Groups claim"
                value={config.attr_groups}
                onChange={e => setConfig({ ...config, attr_groups: e.target.value })}/>
            </Grid>

            {/* Authorization */}
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Authorization</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Default role</InputLabel>
                <Select value={config.default_role} label="Default role"
                  onChange={e => setConfig({ ...config, default_role: e.target.value as any })}>
                  {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={
                <Switch checked={config.jit_provisioning_enabled}
                  onChange={e => setConfig({ ...config, jit_provisioning_enabled: e.target.checked })} />
              } label={
                <Box>
                  <Typography variant="body2">JIT provisioning</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto-create users on first SSO login.
                  </Typography>
                </Box>
              } sx={{ alignItems: 'flex-start' }}/>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} size="small"
                label="Group → role mapping (JSON)"
                value={groupMapText}
                onChange={e => { setGroupMapText(e.target.value); validateGroupMap(e.target.value) }}
                error={!!groupMapError}
                helperText={groupMapError || 'Example: {"arias-admins":"admin","arias-analysts":"analyst"}. First match wins; otherwise default_role applies.'}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }} />
            </Grid>

            {/* Test Connection */}
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Test Connection</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Required before enabling SSO for users. Runs a real login against your IdP and shows what ARIAS extracted — no user is provisioned and no JWT is minted.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" startIcon={testing ? <CircularProgress size={14} /> : <OpenInNew />}
                  onClick={handleTestConnection}
                  disabled={testing || !config.oidc_client_id || (!config.oidc_discovery_url && !config.oidc_issuer_url)}>
                  {testing ? 'Waiting for IdP…' : 'Test Connection'}
                </Button>
                {config.last_test_connection_at && (
                  <Chip size="small" icon={config.last_test_connection_ok ? <CheckCircle/> : <Cancel/>}
                    label={`Last test: ${config.last_test_connection_ok ? 'OK' : 'FAILED'} at ${new Date(config.last_test_connection_at).toLocaleString()}`}
                    color={config.last_test_connection_ok ? 'success' : 'error'} variant="outlined" />
                )}
              </Box>

              {testResult && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: testResult.ok ? 'success.50' : 'error.50',
                          border: '1px solid', borderColor: testResult.ok ? 'success.light' : 'error.light',
                          borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {testResult.ok ? '✓ Test succeeded' : '✗ Test failed'}
                  </Typography>
                  {testResult.errors?.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {testResult.errors.map((e: string, i: number) => <div key={i}>{e}</div>)}
                    </Alert>
                  )}
                  {testResult.mapped && (
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      <div>Email: <strong>{testResult.mapped.email || '(missing)'}</strong></div>
                      <div>Name: <strong>{testResult.mapped.name || '(missing)'}</strong></div>
                      <div>Groups: <strong>{testResult.mapped.groups?.join(', ') || '(none)'}</strong></div>
                      <div>Subject: <strong style={{ wordBreak: 'break-all' }}>{testResult.mapped.subject || '(missing)'}</strong></div>
                      <div>Resolved role: <strong>{testResult.resolved_role}</strong></div>
                      <div>Signature valid: <strong>{String(testResult.signature_valid)}</strong></div>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>

            {/* Enable for users */}
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Tooltip title={!canEnable ? 'Run a successful Test Connection first' : ''}>
                <span>
                  <FormControlLabel control={
                    <Switch checked={config.enabled}
                      disabled={!canEnable && !config.enabled}
                      onChange={e => setConfig({ ...config, enabled: e.target.checked })} />
                  } label={
                    <Box>
                      <Typography variant="body2">Enable SSO for users in this tenant</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Once enabled, users with matching email domain are redirected to your IdP at login. Existing passwords still work alongside.
                      </Typography>
                    </Box>
                  } sx={{ alignItems: 'flex-start' }}/>
                </span>
              </Tooltip>
              {enableBlocked && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  A successful Test Connection is required before saving with this enabled.
                </Alert>
              )}
            </Grid>

            {/* Data handling notice — mirror of the AI Assist pattern */}
            <Grid item xs={12}>
              <Alert severity="info" icon={<Lock fontSize="small" />} sx={{ '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                <strong>Where this data lives:</strong> the client secret is encrypted at rest in our database using a per-environment Fernet key managed via AWS Secrets Manager. ARIAS never logs the secret or returns it to the browser in plaintext.
              </Alert>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {hasSavedConfig && (
          <Button color="error" onClick={handleDelete} sx={{ mr: 'auto' }} startIcon={<SettingsIcon />}>
            Remove configuration
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || enableBlocked}>
          {saving ? 'Saving…' : (hasSavedConfig ? 'Update' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SsoConfigDialog
